/**
 * Attendance View Page — read-only detail.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@yesbos/ui';
import { ArrowLeft, Edit } from 'lucide-react';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium">{value ?? "—"}</dd>
    </div>
  );
}

function fmtTime(ts: unknown): string {
  if (!ts) return '—';
  const s = String(ts);
  if (s.includes('T')) return s.slice(11, 16);
  return s.slice(0, 5);
}

export function AttendanceViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useOne({ resource: 'attendance_logs', id: id! });
  const log = data?.data as Record<string, unknown> | undefined;

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;
  if (!log) return <div className="p-6 text-sm text-destructive">Attendance record not found.</div>;

  const statusColor = log.status === 'present' ? 'default' : log.status === 'absent' ? 'destructive' : 'secondary';

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/attendance')}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl font-semibold">Attendance Record</h1>
          <Badge variant={statusColor as 'default' | 'secondary' | 'destructive'}>{String(log.status ?? '').replace(/_/g, ' ')}</Badge>
        </div>
        <Button variant="outline" onClick={() => navigate(`/attendance/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />Edit
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle>Attendance Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Date" value={String(log.date ?? '').slice(0, 10)} />
            <Field label="Status" value={String(log.status ?? '').replace(/_/g, ' ')} />
            <Field label="Check-In" value={fmtTime(log.check_in_time)} />
            <Field label="Check-Out" value={fmtTime(log.check_out_time)} />
            <Field label="Total Hours" value={log.total_hours ? `${log.total_hours}h` : '—'} />
            <Field label="Marked By" value={String(log.marked_by ?? '')} />
          </dl>
          {log.remarks && (
            <div className="mt-4 space-y-1">
              <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Remarks</dt>
              <dd className="text-sm">{String(log.remarks)}</dd>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AttendanceViewPage;
